import React, { useEffect, useState } from "react";
import {
  Card,
  Steps,
  Descriptions,
  Tag,
  Space,
  Typography,
  Timeline,
  Button,
  Alert,
  message,
  Tooltip,
  Modal,
  Dropdown,
  Collapse,
  Upload,
} from "antd";
import {
  GlobalOutlined,
  WhatsAppOutlined,
  EnvironmentOutlined,
  ShoppingOutlined,
  UserOutlined,
  ContainerOutlined,
  RocketOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  ExclamationCircleOutlined,
  InboxOutlined as ArchiveOutlined,
  SafetyCertificateOutlined,
  CarOutlined,
  GiftOutlined,
  StopOutlined,
  HistoryOutlined,
  DownOutlined,
  InfoCircleOutlined,
  TruckOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { supabase } from "../lib/supabase";
import { VesselFinderMap } from "./VesselFinderMap";
import { useNotifications } from "../hooks/useNotifications";
import { useTranslation } from "react-i18next";
import {
  isStorageOption,
  STORAGE_OPTION_TRANSLATION_KEYS,
} from "../utils/storageOptionUtils";
import type { StorageOption } from "../utils/storageOptionUtils";
import {
  isServiceLevel,
  SERVICE_LEVEL_TRANSLATION_KEYS,
} from "../utils/serviceLevelUtils";
import {
  normalizeBookingStatus,
  statusColors,
  statusSteps,
} from "../utils/bookingStatus";
import { useCompany } from "../hooks/useCompany";
import { generateAndSaveShippingLabel, downloadCombinedProductLabelsPdf } from "../utils/shippingLabel";
import { downloadBlob } from "../utils/labelDownloadUtils";
import { useContainer } from "../hooks/useContainer";
import {
  COMMODITY_INSPECTION_FEE_EUR,
  getEffectiveCommodityInspection,
} from "../utils/commodityInspectionUtils";
import {
  getPostExportLicenseFee,
} from "../utils/postExportLicenseUtils";
import {
  useBookingComments,
  BookingComment,
} from "../hooks/useBookingComments";
import { CombinedTimeline } from "./CombinedTimeline";
import { downloadCommercialInvoice } from "../utils/commercialInvoice";
import { uploadCommercialInvoice } from "../utils/commercialInvoiceUtils";
import { downloadBookingInvoice } from "../utils/bookingInvoiceUtils";
import { downloadOtherDocument } from "../utils/otherDocumentsUtils";
import { downloadCustomerPackingSlip } from "../utils/customerPackingSlipUtils";
import { updateBookingCommercialInvoice } from "../services/bookingService";
import { getCustomerPackingSlips } from "../services/customerPackingSlipService";
import { SendcloudShipment, SendcloudTrackingEvent } from "../types/sendcloud";
import { BookingInvoiceCard } from "./BookingInvoiceCard";
import { BookingTrackerLeanCustomsSection } from "./BookingTrackerLeanCustomsSection";
import { BookingQuoteDisplay } from "./BookingQuoteDisplay";
import { useBookingQuote } from "../hooks/useBookingQuote";
import { useFeatureFlags } from "../contexts/FeatureFlagContext";

const { Title } = Typography;

const TrackerContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 768px) {
    padding: 16px;
    gap: 16px;
  }
`;

const StatusContainer = styled.div`
  width: 100%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 24px;
  overflow-x: auto;

  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 16px;
  }

  .ant-steps {
    min-width: 800px;

    .ant-steps-item {
      margin-bottom: 0;
      padding-inline-end: 8px;
      flex: 1;

      .ant-steps-item-container {
        display: flex;
        align-items: flex-start;

        .ant-steps-item-icon {
          width: 28px;
          height: 28px;
          line-height: 28px;
          font-size: 14px;
          margin-right: 8px;
          flex-shrink: 0;
        }

        .ant-steps-item-content {
          flex: 1;
          min-width: 0;

          .ant-steps-item-title {
            font-size: 13px;
            line-height: 1.4;
            font-weight: 500;
            color: rgba(0, 0, 0, 0.85);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            padding-right: 4px;
          }

          .ant-steps-item-description {
            font-size: 12px;
            max-width: none;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }

      &:hover {
        .ant-steps-item-content {
          .ant-steps-item-title,
          .ant-steps-item-description {
            white-space: normal;
            overflow: visible;
            position: relative;
          }
        }
      }
    }
  }
`;

const ContentContainer = styled.div`
  width: 100%;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const StyledCard = styled(Card)`
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  border-radius: 8px;

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }

  .ant-card-body {
    padding: 24px;

    @media (max-width: 768px) {
      padding: 16px;
    }
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .ant-typography-secondary {
    font-size: 12px;
  }

  .ant-typography-strong {
    font-size: 14px;
  }

  .ant-btn-primary {
    background-color: #1890ff;
    &:hover {
      background-color: #40a9ff;
    }
  }

  .ant-space {
    gap: 8px !important;
  }

  .product-card {
    .ant-card-head {
      min-height: 48px;
      padding: 0 16px;
    }
    .ant-card-head-title {
      padding: 12px 0;
    }
    .ant-card-extra {
      padding: 12px 0;
    }
    .ant-btn-link {
      height: auto;
      padding: 0;
      color: #1890ff;
      &:hover {
        color: #40a9ff;
      }
    }
  }

  .product-details {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  .product-info {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 8px;
    flex: 1;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      width: 100%;
    }
  }

  .button-container {
    display: flex;
    align-items: flex-start;
    min-width: 180px;

    @media (max-width: 768px) {
      margin-top: 8px;
      width: 100%;
    }
  }

  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
    }

    .label {
      color: rgba(0, 0, 0, 0.45);
      margin-right: 8px;

      @media (max-width: 768px) {
        margin-right: 0;
      }
    }
    .value {
      font-weight: 500;
    }
  }

  .shipping-label-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
  }

  .ant-timeline {
    @media (max-width: 768px) {
      .ant-timeline-item {
        padding-bottom: 16px;
      }
      .ant-timeline-item-content {
        margin-left: 16px;
      }
    }

    .ant-timeline-item:last-child {
      padding-bottom: 0;

      .ant-timeline-item-tail {
        display: none;
      }
    }
  }

  .ant-descriptions {
    @media (max-width: 768px) {
      .ant-descriptions-item {
        padding-bottom: 8px;
      }
      .ant-descriptions-item-label {
        padding-bottom: 4px;
      }
    }
  }

  .ant-collapse {
    @media (max-width: 768px) {
      .ant-collapse-header {
        padding: 8px 16px;
      }
      .ant-collapse-content {
        padding: 8px 16px;
      }
    }
  }

  .delivery-info,
  .storage-info {
    @media (max-width: 768px) {
      .ant-typography {
        display: block;
        margin-bottom: 4px;
      }
    }
  }

  .delivery-info {
    @media (max-width: 768px) {
      > div {
        margin-top: 12px;
      }
    }
  }

  .storage-info {
    @media (max-width: 768px) {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .ant-typography {
        margin-left: 0 !important;
      }
    }
  }
`;

const StyledDescriptions = styled(Descriptions)`
  .ant-descriptions-view {
    border: 1px solid #f0f0f0;
    border-radius: 8px;
  }

  @media (max-width: 768px) {
    .ant-descriptions-row {
      display: flex;
      flex-direction: column;

      > th,
      > td {
        width: 100% !important;
        padding: 12px 16px;
        border-bottom: 1px solid #f0f0f0;
      }

      > th {
        background: #fafafa;
        font-weight: 500;
      }

      > td {
        background: white;
      }
    }

    .ant-descriptions-item-label {
      padding: 8px 0;
      color: rgba(0, 0, 0, 0.65);
    }

    .ant-descriptions-item-content {
      padding: 8px 0;
      font-weight: 500;
    }
  }
`;

interface BookingProduct {
  id: number;
  name: string;
  hs_code: string | null;
  gross_weight: number;
  cbm: number;
  value: number;
  ean: number | null;
  shipping_label: string | null;
  pieces: number | null;
  cartons: number | null;
}

interface ShipmentWithTracking extends SendcloudShipment {
  tracking_events?: SendcloudTrackingEvent[];
}

interface BookingDetails {
  id: number;
  reference: string;
  status: string;
  eta?: string | null;
  rejection_reason?: string | null;
  company_id: number;
  transport_type: { id: number; name: string; abbreviation: string };
  booking_type?: { name: string };
  service_level?: string | null;
  booking_incoterm: { name: string };
  origin_port?: { name: string; country: string };
  destination_port?: { name: string; country: string };
  origin_pickup_date: string;
  track_and_trace: string | null;
  supplier?: {
    name: string;
    email: string;
    phone: string;
    country: string;
    city: string;
    address: string;
  };
  products: BookingProduct[];
  created_at: string;
  delivery_type?: "storage" | "address";
  storage_option?: StorageOption | null;
  delivery_details?: Array<{
    id: number;
    country: string;
    city: string;
    postal_code: string;
    street_name: string;
    house_number: string;
    instructions: string | null;
  }>;
  invoices?: Array<{
    id: number;
    booking_invoice_file: string;
    created_at: string;
  }>;
  other_documents?: Array<{
    id: number;
    booking_other_documents_file: string;
    created_at: string;
  }>;
  customer_packing_slips?: Array<{
    id: number;
    booking_packing_slip_file: string;
    created_at: string;
  }>;
  commercial_invoice_file?: string;
  comments: BookingComment[];
  notes?: string | null;
  export_clearance?: boolean | null;
  pick_up_fee?: number | null;
  commodity_inspection?: boolean | null;
  post_export_license?: number | null;
  leancustoms_order_id?: string | null;
  leancustoms_declaration_id?: string | null;
}

interface ProductCardProps {
  product: BookingProduct;
  booking: BookingDetails;
  onEdit: (product: BookingProduct) => void;
  onDelete: (product: BookingProduct) => void;
  isEditable: boolean;
}

// Add helper function for direct downloads
const downloadFile = async (url: string, filename: string) => {
  try {
    console.log("Downloading file from URL:", url);

    // Parse the filename to determine file type
    const fileExt = filename.split(".").pop()?.toLowerCase() || "";

    // Check file extension and content type from URL
    const isPdf = fileExt === "pdf";
    const isPng = fileExt === "png";
    const isJpg = ["jpg", "jpeg"].includes(fileExt);
    const isImage = isPng || isJpg;

    console.log(
      `File detected as: ${
        isPdf ? "PDF" : isImage ? "Image" : "Other"
      } (${fileExt})`
    );

    // For PDFs, open directly in a new tab
    if (isPdf) {
      console.log("Using direct PDF access approach");
      console.log("Opening PDF directly in new tab with signed URL");
      window.open(url, "_blank");
      return;
    }

    // For PNG and JPG images, use a direct download approach
    if (isImage) {
      console.log(`Downloading ${isPng ? "PNG" : "JPG"} image file`);

      // Fetch the image
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Cache-Control": "no-cache",
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed with status: ${response.status}`);
      }

      // Get the response as a blob
      const blob = await response.blob();

      // Create correct content type
      const contentType = isPng ? "image/png" : "image/jpeg";
      const imageBlob = new Blob([blob], { type: contentType });

      // Create URL and download
      const downloadUrl = window.URL.createObjectURL(imageBlob);
      const link = window.document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;

      console.log(`Triggering ${contentType} download`);
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      console.log(`Image download complete: ${filename}`);
      return;
    }

    // For other file types, use default approach
    const requestOptions = {
      method: "GET",
      headers: {
        "Cache-Control": "no-cache",
      },
    };

    console.log("Making fetch request to:", url);
    const response = await fetch(url, requestOptions);

    if (!response.ok) {
      throw new Error(`Download failed with status: ${response.status}`);
    }

    // Create a proper download
    console.log("Creating blob from response");
    const blob = await response.blob();

    // Create a blob with generic content type
    const downloadUrl = window.URL.createObjectURL(
      new Blob([blob], { type: "application/octet-stream" })
    );
    const link = window.document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;

    console.log("Triggering download via click");
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    console.log(`File download complete: ${filename}`);
  } catch (error) {
    console.error("Error downloading file:", error);
    message.error(
      `Error downloading file: ${
        error instanceof Error ? error.message : String(error)
      }`
    );

    // As a fallback for failed downloads, try opening the URL directly in a new tab
    console.log("Attempting fallback: opening URL in new tab");
    window.open(url, "_blank");
  }
};

const ProductCard = ({
  product,
  booking,
  onEdit,
  onDelete,
  isEditable,
}: ProductCardProps) => {
  const { t } = useTranslation();
  const { companyDetails } = useCompany();
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isGeneratingLabel, setIsGeneratingLabel] = useState(false);

  // Define allowed statuses at component scope
  const allowedStatuses = [
    "approved",
    "waiting_for_goods",
    "goods_received_origin",
    "in_transit",
    "customs_pending_clearance",
    "customs_cleared",
    "fyco",
    "out_for_delivery",
    "delivered",
  ];

  useEffect(() => {
    const fetchDownloadUrl = async () => {
      if (product.shipping_label) {
        try {
          const { data, error } = await supabase.storage
            .from("shipping_labels")
            .createSignedUrl(product.shipping_label, 3600);

          if (error) throw error;
          setDownloadUrl(data?.signedUrl || null);
        } catch (error) {
          console.error("Error fetching download URL:", error);
        }
      }
    };

    fetchDownloadUrl();
  }, [product.shipping_label]);

  const handleGenerateLabel = async () => {
    if (!booking.supplier) {
      message.error(t("shippingLabel.noSupplier"));
      return;
    }

    if (!companyDetails?.id) {
      message.error(t("common.noCompanySelected"));
      return;
    }

    setIsGeneratingLabel(true);
    try {
      const labelPath = await generateAndSaveShippingLabel(
        {
          id: booking.id,
          reference: booking.reference,
          transport_type: booking.transport_type,
          booking_incoterm: booking.booking_incoterm,
          supplier: booking.supplier,
        },
        {
          id: product.id,
          name: product.name,
          hs_code: product.hs_code,
          gross_weight: Number(product.gross_weight.toFixed(2)),
          cbm: Number(product.cbm.toFixed(2)),
          pieces: product.pieces,
          cartons: product.cartons,
          ean: product.ean,
          company_id: companyDetails.id,
        },
        companyDetails.name || "Your Company"
      );

      if (!labelPath) {
        throw new Error("Failed to generate shipping label");
      }

      // Update the product with the new shipping label
      const { error: updateError } = await supabase
        .from("booking_product")
        .update({ shipping_label: labelPath })
        .eq("id", product.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Get the download URL for the new label and trigger download
      const { data: urlData } = await supabase.storage
        .from("shipping_labels")
        .createSignedUrl(labelPath, 3600);

      if (urlData?.signedUrl) {
        setDownloadUrl(urlData.signedUrl);
        // Update the product's shipping_label property
        product.shipping_label = labelPath;
        // Trigger direct download
        await downloadFile(
          urlData.signedUrl,
          `shipping-label-${booking.id}-${product.id}.pdf`
        );
      }

      message.success(t("shippingLabel.generateSuccess"));
    } catch (error) {
      console.error("Error generating shipping label:", error);
      message.error(t("shippingLabel.generateError"));
    } finally {
      setIsGeneratingLabel(false);
    }
  };

  const renderLabelButton = () => {
    if (allowedStatuses.includes(booking.status)) {
      if (product.shipping_label && downloadUrl) {
        return (
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() =>
              downloadFile(
                downloadUrl,
                `shipping-label-${booking.id}-${product.id}.pdf`
              )
            }
          >
            {t("shippingLabel.downloadLabel")}
          </Button>
        );
      } else {
        return (
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleGenerateLabel}
            loading={isGeneratingLabel}
          >
            {isGeneratingLabel
              ? t("shippingLabel.generatingLabel")
              : t("shippingLabel.generateAndDownloadLabel")}
          </Button>
        );
      }
    }
    return null;
  };

  return (
    <Card
      className="product-card"
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontWeight: "bold" }}>{product.name}</span>
          {isEditable && (
            <Space>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(product);
                }}
              />
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(product);
                }}
              />
            </Space>
          )}
        </div>
      }
    >
      <div className="product-details">
        <div className="product-info">
          <div className="info-item">
            <Typography.Text type="secondary">
              {t("products.hsCode")}
            </Typography.Text>
            <Typography.Text strong>{product.hs_code || "-"}</Typography.Text>
          </div>
          <div className="info-item">
            <Typography.Text type="secondary">
              {t("products.ean")}
            </Typography.Text>
            <Typography.Text strong>{product.ean || "-"}</Typography.Text>
          </div>
          <div className="info-item">
            <Typography.Text type="secondary">
              {t("products.grossWeight")}
            </Typography.Text>
            <Typography.Text strong>{product.gross_weight} kg</Typography.Text>
          </div>
          <div className="info-item">
            <Typography.Text type="secondary">
              {t("products.cbm")}
            </Typography.Text>
            <Typography.Text strong>{product.cbm} m³</Typography.Text>
          </div>
          <div className="info-item">
            <Typography.Text type="secondary">
              {t("products.value")}
            </Typography.Text>
            <Typography.Text strong>${product.value}</Typography.Text>
          </div>
          <div className="info-item">
            <Typography.Text type="secondary">
              {t("products.pieces")}
            </Typography.Text>
            <Typography.Text strong>{product.pieces || "-"}</Typography.Text>
          </div>
          <div className="info-item">
            <Typography.Text type="secondary">
              {t("products.cartons")}
            </Typography.Text>
            <Typography.Text strong>{product.cartons || "-"}</Typography.Text>
          </div>
        </div>
        <div className="button-container">{renderLabelButton()}</div>
      </div>
    </Card>
  );
};

// Status icons mapping
const statusIcons: Record<string, React.ReactNode> = {
  concept: <FileTextOutlined style={{ fontSize: 20 }} />,
  submitted: <ClockCircleOutlined style={{ fontSize: 20 }} />,
  approved: <CheckCircleOutlined style={{ fontSize: 20 }} />,
  rejected: <ExclamationCircleOutlined style={{ fontSize: 20 }} />,
  waiting_for_goods: <InboxOutlined style={{ fontSize: 20 }} />,
  customs_pending_clearance: (
    <SafetyCertificateOutlined style={{ fontSize: 20 }} />
  ),
  custom_clearance: <SafetyCertificateOutlined style={{ fontSize: 20 }} />,
  customs_cleared: <CheckCircleOutlined style={{ fontSize: 20 }} />,
  fyco: <GlobalOutlined style={{ fontSize: 20 }} />,
  out_for_delivery: <CarOutlined style={{ fontSize: 20 }} />,
  delivered: <GiftOutlined style={{ fontSize: 20 }} />,
  added_to_stock: (
    <InboxOutlined style={{ fontSize: 20, transform: "scale(1.1)" }} />
  ),
  in_transit: <GlobalOutlined style={{ fontSize: 20 }} />,
  cancelled: <StopOutlined style={{ fontSize: 20 }} />,
  archived: <ArchiveOutlined style={{ fontSize: 20 }} />,
  goods_received_origin: <InboxOutlined style={{ fontSize: 20 }} />,
};

export const BookingTracker = ({ bookingId }: { bookingId: string }) => {
  const { t } = useTranslation();
  const { companyDetails } = useCompany();
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shipments, setShipments] = useState<ShipmentWithTracking[]>([]);

  // Commercial invoice upload state
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [appendInvoice] = useState(true);
  const [downloadingAllLabels, setDownloadingAllLabels] = useState(false);

  // Get pricing calculator feature flag
  const { isPricingCalculatorEnabled } = useFeatureFlags();

  const { notifications, loading: notificationsLoading } =
    useNotifications(bookingId);
  const { container } = useContainer(bookingId);
  const { comments: bookingComments, loading: commentsLoading } =
    useBookingComments(Number(bookingId));
  
  // Only fetch quote if pricing calculator is enabled
  const { quote, loading: quoteLoading } = useBookingQuote(
    isPricingCalculatorEnabled ? Number(bookingId) : null
  );

  const fetchSendcloudShipments = async () => {
    try {
      const { data, error } = await supabase
        .from("sendcloud_shipments")
        .select(
          `
          *,
          sendcloud_tracking_events(
            id,
            event_timestamp,
            status,
            message,
            location,
            sendcloud_event_id
          )
        `
        )
        .eq("booking_id", bookingId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching shipments:", error);
        return;
      }

      const shipmentsWithTracking = (data || []).map(
        (
          shipment: SendcloudShipment & {
            sendcloud_tracking_events?: SendcloudTrackingEvent[];
          }
        ) => ({
          ...shipment,
          tracking_events: shipment.sendcloud_tracking_events || [],
        })
      );

      setShipments(shipmentsWithTracking);
    } catch (error) {
      console.error("Error in fetchSendcloudShipments:", error);
    }
  };

  const fetchBooking = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
          *,
          transport_type:booking_transport_types(name, abbreviation),
          booking_type:bookings_type(name),
          booking_incoterm:bookings_incoterm(name),
          origin_port:booking_origin_port(name, country),
          destination_port:booking_destination_port(name, country),
          supplier:booking_supplier(
            name,
            email,
            phone,
            country,
            city,
            address
          ),
          products:booking_product(
            id,
            name,
            hs_code,
            gross_weight,
            cbm,
            value,
            ean,
            shipping_label,
            pieces,
            cartons
          ),
          delivery_details:booking_delivery(
            id,
            country,
            city,
            postal_code,
            street_name,
            house_number,
            instructions
          ),
          invoices:booking_invoice(
            id,
            booking_invoice_file,
            created_at
          ),
          other_documents:booking_other_documents(
            id,
            booking_other_documents_file,
            created_at
          ),
          commercial_invoice_file,
          notes,
          export_clearance,
          pick_up_fee,
          commodity_inspection,
          post_export_license
        `
        )
        .eq("id", bookingId)
        .single();

      if (error) {
        console.error("Error fetching booking:", error);
        setError(error.message);
        return;
      }

      // Fetch customer packing slips separately since they're in a different table
      const customerPackingSlipsResult = await getCustomerPackingSlips(
        Number(bookingId)
      );
      const customerPackingSlips = customerPackingSlipsResult.success
        ? customerPackingSlipsResult.data
        : [];

      setBooking({
        ...data,
        customer_packing_slips: customerPackingSlips,
      });
    } catch (error) {
      console.error("Unexpected error:", error);
      setError(
        error instanceof Error ? error.message : "An unexpected error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooking();
    fetchSendcloudShipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {t("common.retry")}
          </button>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">{t("common.noData")}</p>
        </div>
      </div>
    );
  }

  const leanCustomsOrganizationId = companyDetails?.leancustoms_organization_id;
  const leanCustomsBookingReference = (() => {
    const abbrev = booking.transport_type?.abbreviation ?? "";
    const eff = `EFF${booking.id}${abbrev}`;
    if (container?.reference) return `${container.reference} / ${eff}`;
    return eff;
  })();

  const labelDownloadAllowedStatuses = [
    "approved",
    "waiting_for_goods",
    "goods_received_origin",
    "in_transit",
    "customs_pending_clearance",
    "customs_cleared",
    "fyco",
    "out_for_delivery",
    "delivered",
  ];

  const handleDownloadAllProductLabels = async (
    repeatByCartons: boolean
  ) => {
    if (!booking.supplier) {
      message.error(t("shippingLabel.noSupplier"));
      return;
    }
    if (!companyDetails?.id) {
      message.error(t("common.noCompanySelected"));
      return;
    }
    if (!booking.products.length) {
      message.warning(t("bookings.noProducts"));
      return;
    }

    setDownloadingAllLabels(true);
    try {
      const result = await downloadCombinedProductLabelsPdf({
        booking: {
          id: booking.id,
          reference: booking.reference,
          transport_type: booking.transport_type,
          booking_incoterm: booking.booking_incoterm,
          supplier: booking.supplier,
        },
        products: booking.products.map((product) => ({
          id: product.id,
          name: product.name,
          hs_code: product.hs_code,
          gross_weight: Number(product.gross_weight.toFixed(2)),
          cbm: Number(product.cbm.toFixed(2)),
          pieces: product.pieces,
          cartons: product.cartons,
          ean: product.ean,
          company_id: companyDetails.id,
          shipping_label: product.shipping_label,
        })),
        companyName: companyDetails.name || "Your Company",
        repeatByCartons,
      });

      if (!result) {
        throw new Error("Failed to build combined labels PDF");
      }

      if (result.productUpdates.length > 0) {
        setBooking((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            products: prev.products.map((product) => {
              const update = result.productUpdates.find(
                (u) => u.id === product.id
              );
              return update
                ? { ...product, shipping_label: update.shipping_label }
                : product;
            }),
          };
        });
      }

      downloadBlob(
        result.blob,
        `shipping-labels-EFF${booking.id}${booking.transport_type?.abbreviation || ""}.pdf`
      );
      message.success(
        t("shippingLabel.downloadAllSuccess", {
          count: result.pageCount,
          defaultValue: `Downloaded {{count}} label pages as one PDF`,
        })
      );
    } catch (error) {
      console.error("Error downloading all product labels:", error);
      message.error(t("shippingLabel.downloadAllError"));
    } finally {
      setDownloadingAllLabels(false);
    }
  };

  const effectiveCommodityInspection = getEffectiveCommodityInspection(booking);
  const postExportLicenseFee = getPostExportLicenseFee(booking);

  const isVesselFinderLink = (url: string): boolean => {
    return url.includes("vesselfinder.com");
  };

  const getVesselFinderId = (url: string): string | undefined => {
    try {
      const urlObj = new URL(url);

      // Check for IMO in query parameters first
      const imoFromQuery = urlObj.searchParams.get("imo");
      if (imoFromQuery) {
        return imoFromQuery;
      }

      // If no IMO in query, check pathname for vessel details
      const matches = urlObj.pathname.match(/\/vessels\/details\/(\d+)/);
      if (matches && matches[1]) {
        return matches[1];
      }
    } catch {
      // If URL parsing fails, try direct regex matches
      const imoMatch = url.match(/[?&]imo=(\d+)/);
      if (imoMatch) {
        return imoMatch[1];
      }

      const vesselMatch = url.match(/vessels\/details\/(\d+)/);
      if (vesselMatch) {
        return vesselMatch[1];
      }
    }
    return undefined;
  };

  const handleDownloadCommercialInvoice = async (
    filePath: string,
    index: number = 0
  ) => {
    if (!filePath) {
      message.error("No commercial invoice file available");
      return;
    }

    console.log(`Attempting to download file ${index + 1}:`, filePath);

    try {
      // Show loading message
      const loadingMsg = message.loading(
        `Preparing commercial invoice ${index + 1} for download...`,
        0
      );

      // Use the utility function to download the commercial invoice
      await downloadCommercialInvoice(
        filePath,
        `commercial-invoice-${booking.id}${index > 0 ? `-${index}` : ""}`,
        booking.id
      );

      // Clear loading message
      loadingMsg();
      message.success(`Commercial invoice ${index + 1} download started`);
    } catch (error) {
      console.error(
        `Error downloading commercial invoice ${index + 1}:`,
        error
      );

      // Show error with more details to help debugging
      Modal.error({
        title: "Download Failed",
        content: (
          <div>
            <p>We couldn't access this commercial invoice file.</p>
            <p>
              Error: {error instanceof Error ? error.message : "Unknown error"}
            </p>
            <p>File path: {filePath}</p>
            <p>Please try again or contact support if the issue persists.</p>
          </div>
        ),
      });
    }
  };

  // Add a hasDocuments variable before the return statement in the BookingTracker component
  // Show Documents section if there are existing documents OR if we can upload commercial invoices
  const hasDocuments =
    booking?.commercial_invoice_file ||
    (booking?.invoices && booking.invoices.length > 0) ||
    (booking?.other_documents && booking.other_documents.length > 0) ||
    (booking?.customer_packing_slips &&
      booking.customer_packing_slips.length > 0) ||
    // Always show if we can upload commercial invoices (when none exist)
    (booking && !booking.commercial_invoice_file);

  // Handle commercial invoice upload
  const handleCommercialInvoiceUpload = async () => {
    if (!invoiceFile) {
      message.error(t("bookings.commercialInvoiceUpload.fileRequired"));
      return;
    }

    if (!booking) {
      message.error(t("bookings.commercialInvoiceUpload.bookingNotFound"));
      return;
    }

    setUploadingInvoice(true);
    try {
      // First upload the file to storage
      const uploadResult = await uploadCommercialInvoice(
        booking.id,
        invoiceFile,
        appendInvoice,
        booking.company_id
      );

      console.log("Upload result:", uploadResult);

      if (!uploadResult || uploadResult.error) {
        message.error(t("bookings.commercialInvoiceUpload.uploadError"));
        console.error("Upload error:", uploadResult?.error);
        return;
      }

      // Then update the booking record with the new file path
      const result = await updateBookingCommercialInvoice(
        booking.id,
        uploadResult.path || "",
        appendInvoice
      );

      if (result.error) {
        message.error(t("bookings.commercialInvoiceUpload.updateError"));
        return;
      }

      message.success(t("bookings.commercialInvoiceUpload.uploadSuccess"));
      setInvoiceFile(null);

      // Refresh booking data
      await fetchBooking();
    } catch (error) {
      console.error("Error uploading commercial invoice:", error);
      message.error(t("bookings.commercialInvoiceUpload.uploadError"));
    } finally {
      setUploadingInvoice(false);
    }
  };

  // Upload props for commercial invoice
  const uploadProps = {
    beforeUpload: (file: File) => {
      const isValidType =
        file.type === "application/pdf" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel";
      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isValidType) {
        message.error(t("bookings.commercialInvoiceUpload.fileTypeError"));
        return Upload.LIST_IGNORE;
      }

      if (!isLt5M) {
        message.error(t("bookings.commercialInvoiceUpload.fileSizeError"));
        return Upload.LIST_IGNORE;
      }

      setInvoiceFile(file);
      return false;
    },
    fileList: invoiceFile
      ? [{ uid: "1", name: invoiceFile.name, status: "done" as const }]
      : [],
    onRemove: () => {
      setInvoiceFile(null);
      return true;
    },
  };

  // Process commercial invoice files if comma-separated
  const commercialInvoiceFiles = booking?.commercial_invoice_file
    ? booking.commercial_invoice_file.split(",")
    : [];

  const handleDownloadOtherDocument = async (document: {
    id: number;
    booking_other_documents_file: string;
  }) => {
    if (!document.booking_other_documents_file) {
      message.error("No file path available");
      return;
    }

    const cleanPath = document.booking_other_documents_file.replace(/^\//, "");
    const fileExt = cleanPath.split(".").pop()?.toLowerCase() || "pdf";
    const fileName = `document-${booking.id}-${document.id}.${fileExt}`;

    try {
      await downloadOtherDocument(cleanPath, fileName, booking.id);
    } catch (error) {
      console.error("Error downloading other document:", error);
      message.error("Failed to download document. Please try again later.");
    }
  };

  const handleDownloadCustomerPackingSlip = async (document: {
    id: number;
    booking_packing_slip_file: string;
  }) => {
    if (!document.booking_packing_slip_file) {
      message.error("No file path available");
      return;
    }

    const cleanPath = document.booking_packing_slip_file.replace(/^\//, "");
    const fileExt = cleanPath.split(".").pop()?.toLowerCase() || "pdf";
    const fileName = `customer-packing-slip-${booking.id}-${document.id}.${fileExt}`;

    try {
      await downloadCustomerPackingSlip(cleanPath, fileName, booking.id);
    } catch (error) {
      console.error("Error downloading customer packing slip:", error);
      message.error(
        "Failed to download customer packing slip. Please try again later."
      );
    }
  };

  const handleDownloadBookingInvoiceFile = async (invoice: {
    id: number;
    booking_invoice_file: string | null;
  }) => {
    if (!invoice.booking_invoice_file) {
      message.error("No file path available");
      return;
    }
    const cleanPath = invoice.booking_invoice_file.replace(/^\//, "");
    const fileExt =
      invoice.booking_invoice_file?.split(".").pop()?.toLowerCase() || "pdf";
    const fileName = `invoice-${booking.id}-${invoice.id}.${fileExt}`;
    try {
      await downloadBookingInvoice(cleanPath, fileName, booking.id);
    } catch (error) {
      console.error("Error downloading booking invoice:", error);
      message.error("Failed to download invoice. Please try again later.");
    }
  };

  return (
    <TrackerContainer>
      {booking.status === "rejected" && booking.rejection_reason && (
        <Alert
          message={t("bookings.rejectionReason")}
          description={booking.rejection_reason}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
          action={
            <Button
              type="primary"
              onClick={() => {
                window.location.href = `/bookings/${bookingId}/edit`;
              }}
            >
              {t("common.editAndResubmit")}
            </Button>
          }
        />
      )}

      {booking.notes && (
        <Alert
          message={t("bookings.notes")}
          description={booking.notes}
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <StatusContainer>
        <Steps
          direction="horizontal"
          current={
            statusSteps[
              normalizeBookingStatus(booking.status) as keyof typeof statusSteps
            ] || 0
          }
          items={[
            {
              title: t("status.concept"),
              description: t("status.concept_description"),
              icon: statusIcons.concept,
            },
            {
              title: t("status.submitted"),
              description: t("status.submitted_description"),
              icon: statusIcons.submitted,
            },
            {
              title: t("status.approved"),
              description: t("status.approved_description"),
              icon: statusIcons.approved,
            },
            {
              title: t("status.waiting_for_goods"),
              description: t("status.waiting_for_goods_description"),
              icon: statusIcons.waiting_for_goods,
            },
            {
              title: t("status.goods_received_origin"),
              description: t("status.goods_received_origin_description"),
              icon: statusIcons.goods_received_origin,
            },
            {
              title: t("status.in_transit"),
              description: t("status.in_transit_description"),
              icon: statusIcons.in_transit,
            },
            {
              title: t("status.customs_pending_clearance"),
              description: t("status.customs_pending_clearance_description"),
              icon: statusIcons.customs_pending_clearance,
            },
            {
              title: t("status.custom_clearance"),
              description: t("status.custom_clearance_description"),
              icon: statusIcons.custom_clearance,
            },
            {
              title: t("status.customs_cleared"),
              description: t("status.customs_cleared_description"),
              icon: statusIcons.customs_cleared,
            },
            {
              title: t("status.fyco"),
              description: t("status.fyco_description"),
              icon: statusIcons.fyco,
            },
            {
              title: t("status.out_for_delivery"),
              description: t("status.out_for_delivery_description"),
              icon: statusIcons.out_for_delivery,
            },
            {
              title:
                booking.status === "added_to_stock"
                  ? t("status.added_to_stock")
                  : t("status.delivered"),
              description:
                booking.status === "added_to_stock"
                  ? t("status.added_to_stock_description")
                  : t("status.delivered_description"),
              icon:
                booking.status === "added_to_stock"
                  ? statusIcons.added_to_stock
                  : statusIcons.delivered,
            },
          ]}
        />
      </StatusContainer>

      <StyledCard
        title={
          <Space>
            <HistoryOutlined />
            {t("bookings.timeline")}
          </Space>
        }
        style={{ marginTop: 16, marginBottom: 16 }}
      >
        <CombinedTimeline
          comments={bookingComments}
          notifications={notifications}
          loading={commentsLoading || notificationsLoading}
        />
      </StyledCard>

      {/* Sendcloud Shipments Section - moved to top level */}
      {shipments && shipments.length > 0 && (
        <StyledCard
          title={
            <Space>
              <TruckOutlined />
              {t("tracking.lastMileDelivery", "Last Mile Delivery")}
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          <Collapse
            size="small"
            ghost
            items={shipments.map((shipment) => {
              const getShipmentStatusColor = (status: string) => {
                const statusLower = status.toLowerCase();

                if (
                  statusLower.includes("delivered") ||
                  statusLower.includes("completed")
                ) {
                  return "success";
                }
                if (
                  statusLower.includes("transit") ||
                  statusLower.includes("en_route") ||
                  statusLower.includes("picked_up")
                ) {
                  return "processing";
                }
                if (
                  statusLower.includes("ready") ||
                  statusLower.includes("pending")
                ) {
                  return "warning";
                }
                if (
                  statusLower.includes("error") ||
                  statusLower.includes("failed")
                ) {
                  return "error";
                }
                return "default";
              };

              const formatShipmentStatus = (status: string) => {
                return status
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase());
              };

              // Get the latest tracking event for display in the label
              const latestEvent =
                shipment.tracking_events && shipment.tracking_events.length > 0
                  ? shipment.tracking_events.sort(
                      (a, b) =>
                        new Date(b.event_timestamp).getTime() -
                        new Date(a.event_timestamp).getTime()
                    )[0]
                  : null;

              return {
                key: shipment.id,
                label: (
                  <Space
                    direction="vertical"
                    size={4}
                    style={{ width: "100%" }}
                  >
                    <Space>
                      <Tag color={getShipmentStatusColor(shipment.status)}>
                        {formatShipmentStatus(shipment.status)}
                      </Tag>
                      {shipment.carrier_name && (
                        <Typography.Text type="secondary">
                          {shipment.carrier_name}
                        </Typography.Text>
                      )}
                      {shipment.tracking_number && (
                        <Typography.Text code style={{ fontSize: "12px" }}>
                          {shipment.tracking_number}
                        </Typography.Text>
                      )}
                    </Space>
                    {latestEvent && (
                      <Space size={8}>
                        <Tag
                          color={getShipmentStatusColor(latestEvent.status)}
                          style={{ fontSize: "11px", margin: 0 }}
                        >
                          {formatShipmentStatus(latestEvent.status)}
                        </Tag>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: "12px" }}
                        >
                          {dayjs(latestEvent.event_timestamp).format(
                            "DD/MM/YYYY HH:mm"
                          )}
                        </Typography.Text>
                        <Typography.Text style={{ fontSize: "12px" }}>
                          {latestEvent.message}
                        </Typography.Text>
                        {latestEvent.location && (
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: "11px" }}
                          >
                            📍 {latestEvent.location}
                          </Typography.Text>
                        )}
                      </Space>
                    )}
                  </Space>
                ),
                children: (
                  <div>
                    <Descriptions size="small" column={1} bordered>
                      {shipment.tracking_number && (
                        <Descriptions.Item
                          label={t(
                            "tracking.trackingNumber",
                            "Tracking Number"
                          )}
                        >
                          <Space>
                            <Typography.Text code>
                              {shipment.tracking_number}
                            </Typography.Text>
                            {shipment.tracking_url && (
                              <Button
                                type="link"
                                size="small"
                                icon={<EnvironmentOutlined />}
                                onClick={() =>
                                  window.open(shipment.tracking_url!, "_blank")
                                }
                              >
                                {t("tracking.trackOnline", "Track Online")}
                              </Button>
                            )}
                          </Space>
                        </Descriptions.Item>
                      )}

                      {shipment.carrier_name && (
                        <Descriptions.Item
                          label={t("tracking.carrier", "Carrier")}
                        >
                          <Space>
                            <img
                              src={`https://cdn.sendcloud.com/global-media/${shipment.carrier_name.toLowerCase()}/img/logo.svg`}
                              alt={shipment.carrier_name}
                              style={{
                                height: "20px",
                                maxWidth: "60px",
                                objectFit: "contain",
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                            <Typography.Text>
                              {shipment.carrier_name}
                            </Typography.Text>
                          </Space>
                        </Descriptions.Item>
                      )}

                      {shipment.shipping_method && (
                        <Descriptions.Item
                          label={t(
                            "tracking.shippingMethod",
                            "Shipping Method"
                          )}
                        >
                          {shipment.shipping_method}
                        </Descriptions.Item>
                      )}

                      <Descriptions.Item
                        label={t("tracking.destination", "Destination")}
                      >
                        <div>
                          <Typography.Text strong>
                            {shipment.to_name}
                          </Typography.Text>
                          {shipment.to_company_name && (
                            <>
                              <br />
                              <Typography.Text>
                                {shipment.to_company_name}
                              </Typography.Text>
                            </>
                          )}
                          <br />
                          <Typography.Text type="secondary">
                            {shipment.to_street_name} {shipment.to_house_number}
                            , {shipment.to_city}
                            <br />
                            {(() => {
                              try {
                                const countryData =
                                  typeof shipment.to_country === "string"
                                    ? JSON.parse(shipment.to_country)
                                    : shipment.to_country;
                                return countryData?.name || shipment.to_country;
                              } catch {
                                return shipment.to_country;
                              }
                            })()}
                          </Typography.Text>
                        </div>
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={t("tracking.created", "Created")}
                      >
                        {dayjs(shipment.created_at).format("DD/MM/YYYY HH:mm")}
                      </Descriptions.Item>
                    </Descriptions>

                    {shipment.tracking_events &&
                      shipment.tracking_events.length > 0 && (
                        <div style={{ marginTop: 16 }}>
                          <Typography.Text
                            strong
                            style={{ display: "block", marginBottom: 8 }}
                          >
                            <HistoryOutlined style={{ marginRight: 8 }} />
                            {t("tracking.trackingHistory", "Tracking History")}
                          </Typography.Text>
                          <Timeline
                            mode="left"
                            items={shipment.tracking_events
                              .filter((event) => event.status !== "no-label") // Filter out no-label events
                              .sort(
                                (a, b) =>
                                  new Date(b.event_timestamp).getTime() -
                                  new Date(a.event_timestamp).getTime()
                              )
                              .map((event) => ({
                                color: getShipmentStatusColor(event.status),
                                children: (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "12px",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Tag
                                      color={getShipmentStatusColor(
                                        event.status
                                      )}
                                      style={{
                                        margin: 0,
                                        minWidth: "120px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {formatShipmentStatus(event.status)}
                                    </Tag>
                                    <Typography.Text
                                      type="secondary"
                                      style={{
                                        fontSize: "13px",
                                        minWidth: "130px",
                                      }}
                                    >
                                      {dayjs(event.event_timestamp).format(
                                        "DD/MM/YYYY HH:mm"
                                      )}
                                    </Typography.Text>
                                    {event.message && (
                                      <Typography.Text
                                        style={{ fontSize: "13px", flex: 1 }}
                                      >
                                        {event.message}
                                      </Typography.Text>
                                    )}
                                    {event.location && (
                                      <Typography.Text
                                        type="secondary"
                                        style={{
                                          fontSize: "12px",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "4px",
                                        }}
                                      >
                                        <EnvironmentOutlined />
                                        {event.location}
                                      </Typography.Text>
                                    )}
                                  </div>
                                ),
                              }))}
                          />
                        </div>
                      )}
                  </div>
                ),
              };
            })}
          />
        </StyledCard>
      )}

      <ContentContainer>
        <HeaderContainer>
          <Title level={2}>
            {booking && (
              <>
                Booking Details - EFF{booking.id}
                {booking.transport_type?.abbreviation || ""}
                {booking.reference && ` (${booking.reference})`}
              </>
            )}
          </Title>
          <Space>
            {booking && (
              <Button
                type="primary"
                icon={<WhatsAppOutlined />}
                style={{ backgroundColor: "#25D366", borderColor: "#25D366" }}
                onClick={() =>
                  window.open(
                    `https://wa.me/31850607063?text=${encodeURIComponent(
                      `${t("common.contactSupport")} EFF${booking.id}${
                        booking.transport_type?.abbreviation || ""
                      }`
                    )}`,
                    "_blank"
                  )
                }
              >
                {t("common.contactSupport")} EFF{booking.id}
                {booking.transport_type?.abbreviation || ""}
              </Button>
            )}
          </Space>
        </HeaderContainer>

        {/* Price Quote Section - Only shown if quote exists */}
        {quote && (
          <BookingQuoteDisplay quote={quote} loading={quoteLoading} />
        )}

        {/* Invoice Section - Only shown if invoice exists */}
        {booking?.company_id && (
          <BookingInvoiceCard
            bookingId={Number(bookingId)}
            companyId={booking.company_id}
          />
        )}

        {hasDocuments && (
          <StyledCard
            title={
              <Space>
                <FileTextOutlined />
                {t("bookings.documents", "Documents")}
              </Space>
            }
            style={{ marginBottom: "24px" }}
          >
            <Space size="small" wrap>
              {commercialInvoiceFiles.length > 0 && (
                <>
                  {commercialInvoiceFiles.length === 1 ? (
                    <Button
                      key="commercial-invoice-single"
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={() =>
                        handleDownloadCommercialInvoice(
                          commercialInvoiceFiles[0],
                          0
                        )
                      }
                    >
                      {t("bookings.commercialInvoice", "Commercial Invoice")}
                    </Button>
                  ) : (
                    <Dropdown
                      menu={{
                        items: commercialInvoiceFiles.map(
                          (filePath, index) => ({
                            key: `commercial-invoice-${index}`,
                            label: `${t(
                              "bookings.commercialInvoice",
                              "Commercial Invoice"
                            )} ${index + 1}`,
                            icon: <DownloadOutlined />,
                            onClick: () =>
                              handleDownloadCommercialInvoice(filePath, index),
                          })
                        ),
                      }}
                    >
                      <Button type="link">
                        {t("bookings.commercialInvoice", "Commercial Invoice")}{" "}
                        <span>({commercialInvoiceFiles.length})</span>{" "}
                        <DownOutlined />
                      </Button>
                    </Dropdown>
                  )}
                </>
              )}

              {/* Commercial Invoice Upload Section */}
              {commercialInvoiceFiles.length === 0 && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    border: "1px dashed #d9d9d9",
                    borderRadius: "6px",
                  }}
                >
                  <div style={{ textAlign: "center", marginBottom: "16px" }}>
                    <UploadOutlined
                      style={{
                        fontSize: "24px",
                        color: "#1890ff",
                        marginBottom: "8px",
                      }}
                    />
                    <div style={{ color: "#666", marginBottom: "8px" }}>
                      {t("bookings.commercialInvoiceUpload.noInvoiceUploaded")}
                    </div>
                    <div style={{ fontSize: "12px", color: "#999" }}>
                      {t("bookings.commercialInvoiceUpload.uploadInstructions")}
                    </div>
                  </div>

                  <Upload {...uploadProps} style={{ width: "100%" }}>
                    <Button icon={<UploadOutlined />} style={{ width: "100%" }}>
                      {t("bookings.commercialInvoiceUpload.selectFile")}
                    </Button>
                  </Upload>

                  {invoiceFile && (
                    <div style={{ marginTop: "12px", textAlign: "center" }}>
                      <Button
                        type="primary"
                        onClick={handleCommercialInvoiceUpload}
                        disabled={!invoiceFile}
                        loading={uploadingInvoice}
                        style={{ width: "100%" }}
                      >
                        {t("bookings.commercialInvoiceUpload.uploadButton")}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {booking?.invoices && booking.invoices.length > 0 && (
                <>
                  {booking.invoices.length === 1 ? (
                    <Button
                      key={booking.invoices[0].id}
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={() =>
                        handleDownloadBookingInvoiceFile(booking.invoices![0])
                      }
                    >
                      {t("bookings.invoice")}
                    </Button>
                  ) : (
                    <Dropdown
                      menu={{
                        items: booking.invoices.map((invoice, index) => ({
                          key: `invoice-${invoice.id}`,
                          label: `${t("bookings.invoice")} ${index + 1}`,
                          icon: <DownloadOutlined />,
                          onClick: () =>
                            handleDownloadBookingInvoiceFile(invoice),
                        })),
                      }}
                    >
                      <Button type="link">
                        {t("bookings.invoice")}{" "}
                        <span>({booking.invoices.length})</span>{" "}
                        <DownOutlined />
                      </Button>
                    </Dropdown>
                  )}
                </>
              )}

              {/* Other Documents */}
              {booking?.other_documents &&
                booking.other_documents.length > 0 && (
                  <>
                    {booking.other_documents.length === 1 ? (
                      <Button
                        key={booking.other_documents[0].id}
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={() =>
                          handleDownloadOtherDocument(
                            booking.other_documents![0]
                          )
                        }
                      >
                        {t("bookings.otherDocument")}
                      </Button>
                    ) : (
                      <Dropdown
                        menu={{
                          items: booking.other_documents.map(
                            (document, index) => ({
                              key: `other-document-${document.id}`,
                              label: `${t("bookings.otherDocument")} ${
                                index + 1
                              }`,
                              icon: <DownloadOutlined />,
                              onClick: () =>
                                handleDownloadOtherDocument(document),
                            })
                          ),
                        }}
                      >
                        <Button type="link">
                          {t("bookings.otherDocument")}{" "}
                          <span>({booking.other_documents.length})</span>{" "}
                          <DownOutlined />
                        </Button>
                      </Dropdown>
                    )}
                  </>
                )}

              {/* Customer Packing Slips */}
              {booking?.customer_packing_slips &&
                booking.customer_packing_slips.length > 0 && (
                  <>
                    {booking.customer_packing_slips.length === 1 ? (
                      <Button
                        key={booking.customer_packing_slips[0].id}
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={() =>
                          handleDownloadCustomerPackingSlip(
                            booking.customer_packing_slips![0]
                          )
                        }
                      >
                        {t(
                          "bookings.customerPackingSlip",
                          "Customer Packing Slip"
                        )}
                      </Button>
                    ) : (
                      <Dropdown
                        menu={{
                          items: booking.customer_packing_slips.map(
                            (document, index) => ({
                              key: `customer-packing-slip-${document.id}`,
                              label: `${t(
                                "bookings.customerPackingSlip",
                                "Customer Packing Slip"
                              )} ${index + 1}`,
                              icon: <DownloadOutlined />,
                              onClick: () =>
                                handleDownloadCustomerPackingSlip(document),
                            })
                          ),
                        }}
                      >
                        <Button type="link">
                          {t(
                            "bookings.customerPackingSlip",
                            "Customer Packing Slip"
                          )}{" "}
                          <span>({booking.customer_packing_slips.length})</span>{" "}
                          <DownOutlined />
                        </Button>
                      </Dropdown>
                    )}
                  </>
                )}
            </Space>
          </StyledCard>
        )}

        {leanCustomsOrganizationId && booking.company_id ? (
          <BookingTrackerLeanCustomsSection
            bookingId={booking.id}
            bookingReference={leanCustomsBookingReference}
            organizationId={leanCustomsOrganizationId}
            bookingCompanyId={booking.company_id}
            orderId={booking.leancustoms_order_id ?? null}
            declarationId={booking.leancustoms_declaration_id ?? null}
          />
        ) : null}

        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <StyledCard
            title={
              <Space>
                {booking.transport_type && (
                  <>
                    {booking.transport_type.id === 1 ? (
                      <RocketOutlined />
                    ) : (
                      <ContainerOutlined />
                    )}
                    {t("bookingWizard.reviewDetails.transportDetails")}
                  </>
                )}
              </Space>
            }
          >
            <StyledDescriptions bordered column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label={t("bookings.transportType")}>
                {booking.transport_type?.name || "-"}
              </Descriptions.Item>
              {booking.booking_type && (
                <Descriptions.Item label={t("bookings.bookingType")}>
                  {booking.booking_type.name}
                </Descriptions.Item>
              )}
              {booking.service_level &&
                isServiceLevel(booking.service_level) && (
                  <Descriptions.Item label={t("bookings.serviceLevel")}>
                    {t(SERVICE_LEVEL_TRANSLATION_KEYS[booking.service_level])}
                  </Descriptions.Item>
                )}
              <Descriptions.Item label={t("bookings.incoterm")}>
                {booking.booking_incoterm?.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("bookings.cargoReadinessDate")}>
                {booking.origin_pickup_date
                  ? dayjs(booking.origin_pickup_date).format("DD/MM/YYYY")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("bookings.status")}>
                <Tag
                  color={
                    statusColors[
                      normalizeBookingStatus(
                        booking.status
                      ) as keyof typeof statusColors
                    ]
                  }
                >
                  {t(`status.${booking.status}`)}
                </Tag>
              </Descriptions.Item>
              {(() => {
                // Priority: booking.eta (override) > container.eta_customer
                const finalEta = booking.eta || container?.eta_customer;
                if (!finalEta) return null;

                return (
                  <Descriptions.Item
                    label={
                      <Tooltip
                        title={t(
                          "bookings.customerETATooltip",
                          "Expected arrival of your goods at your location. This date factors in port arrival, customs clearance, and final delivery time based on container information."
                        )}
                      >
                        <span>
                          {t("bookings.finalETA", "ETA")}{" "}
                          <InfoCircleOutlined
                            style={{ marginLeft: 5, color: "#1890ff" }}
                          />
                        </span>
                      </Tooltip>
                    }
                  >
                    {dayjs(finalEta).format("DD/MM/YYYY")}
                  </Descriptions.Item>
                );
              })()}
            </StyledDescriptions>
          </StyledCard>

          {/* Extra Services */}
          {(booking.export_clearance !== null ||
            booking.pick_up_fee !== null ||
            booking.commodity_inspection !== null ||
            booking.post_export_license !== null) && (
            <StyledCard
              title={
                <Space>
                  <GiftOutlined />
                  {t("bookings.extraServices")}
                </Space>
              }
            >
              <StyledDescriptions bordered column={{ xs: 1, sm: 2 }}>
                {booking.export_clearance !== null && (
                  <Descriptions.Item label={t("bookings.exportClearance")}>
                    {booking.export_clearance ? (
                      <Space>
                        <Tag color="green">
                          {t("bookings.exportClearanceRequired")}
                        </Tag>
                        <span style={{ color: "#52c41a", fontWeight: "bold" }}>
                          €45.00
                        </span>
                      </Space>
                    ) : (
                      <Tag color="default">
                        {t("bookings.exportClearanceNotRequired")}
                      </Tag>
                    )}
                  </Descriptions.Item>
                )}
                {booking.commodity_inspection !== null && (
                  <Descriptions.Item label={t("bookings.commodityInspection")}>
                    {effectiveCommodityInspection ? (
                      <Space>
                        <Tag color="green">
                          {t("bookings.commodityInspectionRequired")}
                        </Tag>
                        <span style={{ color: "#52c41a", fontWeight: "bold" }}>
                          €{COMMODITY_INSPECTION_FEE_EUR.toFixed(2)}
                        </span>
                      </Space>
                    ) : (
                      <Tag color="default">
                        {t("bookings.commodityInspectionNotRequired")}
                      </Tag>
                    )}
                  </Descriptions.Item>
                )}
                {postExportLicenseFee != null && (
                  <Descriptions.Item label={t("bookings.postExportLicense")}>
                    €{postExportLicenseFee.toFixed(2)}
                  </Descriptions.Item>
                )}
                {booking.pick_up_fee !== null &&
                  booking.pick_up_fee !== undefined && (
                    <Descriptions.Item label={t("bookings.pickUpFee")}>
                      €{booking.pick_up_fee.toFixed(2)}
                    </Descriptions.Item>
                  )}
              </StyledDescriptions>
            </StyledCard>
          )}

          {/* Container Information */}
          {container && (
            <StyledCard
              title={
                <Space>
                  <ContainerOutlined />
                  {t("bookings.containerInformation")}
                </Space>
              }
            >
              <StyledDescriptions bordered column={{ xs: 1, sm: 2 }}>
                <Descriptions.Item label={t("bookings.containerReference")}>
                  {container.reference || "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Tooltip
                      title={t(
                        "bookings.containerETDTooltip",
                        "Estimated departure date of the container from origin port"
                      )}
                    >
                      <span>
                        {t("bookings.containerETD")}{" "}
                        <InfoCircleOutlined
                          style={{ marginLeft: 5, color: "#1890ff" }}
                        />
                      </span>
                    </Tooltip>
                  }
                >
                  {container.delayed_etd || container.etd
                    ? dayjs(container.delayed_etd || container.etd).format(
                        "DD/MM/YYYY"
                      )
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Tooltip
                      title={t(
                        "bookings.containerETATooltip",
                        "Estimated arrival date of the container at destination port"
                      )}
                    >
                      <span>
                        {t("bookings.containerETA")}{" "}
                        <InfoCircleOutlined
                          style={{ marginLeft: 5, color: "#1890ff" }}
                        />
                      </span>
                    </Tooltip>
                  }
                >
                  {container.eta
                    ? dayjs(container.eta).format("DD/MM/YYYY")
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("bookings.containerStatusLabel")}>
                  <Tag
                    color={
                      container.status === "arrived"
                        ? "success"
                        : container.status === "departed"
                        ? "processing"
                        : "default"
                    }
                  >
                    {t(`bookings.containerStatus.${container.status}`)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t("bookings.originPort")}>
                  {container.origin_port ? (
                    <>
                      {container.origin_port.name}
                      {container.origin_port.country &&
                        ` (${container.origin_port.country})`}
                    </>
                  ) : (
                    "-"
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t("bookings.destinationPort")}>
                  {container.destination_port ? (
                    <>
                      {container.destination_port.name}
                      {container.destination_port.country &&
                        ` (${container.destination_port.country})`}
                    </>
                  ) : (
                    "-"
                  )}
                </Descriptions.Item>
              </StyledDescriptions>
            </StyledCard>
          )}

          {/* Tracking Information */}
          <StyledCard
            title={
              <Space>
                <EnvironmentOutlined />
                {t("bookings.trackingInformation")}
              </Space>
            }
          >
            {container?.track_and_trace ? (
              isVesselFinderLink(container.track_and_trace) ? (
                <VesselFinderMap
                  imo={getVesselFinderId(container.track_and_trace)}
                />
              ) : (
                <iframe
                  src={container.track_and_trace}
                  style={{ width: "100%", height: "400px", border: "none" }}
                  title="Track and Trace"
                />
              )
            ) : (
              <Alert
                message={t("bookings.noTrackingInformation")}
                type="info"
                showIcon
              />
            )}
          </StyledCard>

          {/* Delivery Details */}
          <StyledCard
            title={
              <Space>
                <EnvironmentOutlined />
                {t("bookingWizard.deliveryDetails.title")}
              </Space>
            }
          >
            <StyledDescriptions bordered column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item
                label={t("bookingWizard.deliveryDetails.deliveryType")}
                span={2}
              >
                {booking.delivery_details &&
                booking.delivery_details.length > 0 ? (
                  <div className="delivery-info">
                    <Tag color="green">
                      {t("bookingWizard.deliveryDetails.address")}
                    </Tag>
                    <div style={{ marginTop: 8 }}>
                      <Typography.Text strong>
                        {booking.delivery_details[0].street_name}{" "}
                        {booking.delivery_details[0].house_number}
                      </Typography.Text>
                      <br />
                      <Typography.Text>
                        {booking.delivery_details[0].postal_code}{" "}
                        {booking.delivery_details[0].city}
                      </Typography.Text>
                      <br />
                      <Typography.Text>
                        {booking.delivery_details[0].country}
                      </Typography.Text>
                      {booking.delivery_details[0].instructions && (
                        <>
                          <br />
                          <Typography.Text italic>
                            {booking.delivery_details[0].instructions}
                          </Typography.Text>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="storage-info">
                    <Tag color="blue">
                      {t("bookingWizard.deliveryDetails.storage")}
                    </Tag>
                    {booking.storage_option &&
                      isStorageOption(booking.storage_option) && (
                        <Tag color="purple" style={{ marginLeft: 8 }}>
                          {t(
                            STORAGE_OPTION_TRANSLATION_KEYS[
                              booking.storage_option
                            ]
                          )}
                        </Tag>
                      )}
                    <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                      {t("bookingWizard.warehouseStorageMessage")}
                    </Typography.Text>
                  </div>
                )}
              </Descriptions.Item>
            </StyledDescriptions>
          </StyledCard>

          <StyledCard
            title={
              <Space>
                <UserOutlined />
                {t("bookings.supplierInformation")}
              </Space>
            }
          >
            {booking.supplier ? (
              <StyledDescriptions bordered column={{ xs: 1, sm: 2 }}>
                <Descriptions.Item label={t("bookings.supplierName")}>
                  {booking.supplier.name}
                </Descriptions.Item>
                <Descriptions.Item label={t("common.email")}>
                  {booking.supplier.email}
                </Descriptions.Item>
                <Descriptions.Item label={t("common.phone")}>
                  {booking.supplier.phone}
                </Descriptions.Item>
                <Descriptions.Item label={t("common.location")}>
                  {booking.supplier.city}, {booking.supplier.country}
                </Descriptions.Item>
              </StyledDescriptions>
            ) : (
              <Alert
                message={t("bookings.noSupplierInformation")}
                type="info"
                showIcon
              />
            )}
          </StyledCard>

          <StyledCard
            title={
              <Space>
                <ShoppingOutlined />
                {t("bookings.products")}
              </Space>
            }
            extra={
              booking.products.length > 0 &&
              labelDownloadAllowedStatuses.includes(booking.status) ? (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "once",
                        label: t(
                          "shippingLabel.downloadAllOnce",
                          "1 label per product"
                        ),
                        onClick: () => handleDownloadAllProductLabels(false),
                      },
                      {
                        key: "by-cartons",
                        label: t(
                          "shippingLabel.downloadAllByCartons",
                          "1 label per carton"
                        ),
                        onClick: () => handleDownloadAllProductLabels(true),
                      },
                    ],
                  }}
                  trigger={["click"]}
                  disabled={downloadingAllLabels}
                >
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    loading={downloadingAllLabels}
                  >
                    {t("shippingLabel.downloadAllLabels", "in 1 bestand")}
                  </Button>
                </Dropdown>
              ) : null
            }
          >
            {booking.products.length > 0 ? (
              <>
                <Timeline>
                  {booking.products.map((product, index) => (
                    <Timeline.Item key={index}>
                      <ProductCard
                        product={product}
                        booking={booking}
                        onEdit={() => {
                          // Edit functionality will be implemented later
                        }}
                        onDelete={() => {
                          // Delete functionality will be implemented later
                        }}
                        isEditable={false}
                      />
                    </Timeline.Item>
                  ))}
                </Timeline>

                {/* Products Summary Section */}
                <Card
                  title={t("bookings.productsTotals", "Product Totals")}
                  style={{ marginTop: 16 }}
                >
                  <StyledDescriptions bordered column={{ xs: 1, sm: 3 }}>
                    <Descriptions.Item
                      label={t(
                        "products.totalGrossWeight",
                        "Total Gross Weight"
                      )}
                    >
                      {booking.products
                        .reduce(
                          (sum, product) => sum + (product.gross_weight || 0),
                          0
                        )
                        .toFixed(2)}{" "}
                      {t("products.kg", "kg")}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("products.totalCbm", "Total CBM")}
                    >
                      {booking.products
                        .reduce((sum, product) => sum + (product.cbm || 0), 0)
                        .toFixed(3)}{" "}
                      {t("products.cubicMeter", "m³")}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("products.totalValue", "Total Value")}
                    >
                      {t("products.currencySymbol", "$")}
                      {booking.products
                        .reduce((sum, product) => sum + (product.value || 0), 0)
                        .toFixed(2)}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("products.totalPieces", "Total Pieces")}
                    >
                      {booking.products.reduce(
                        (sum, product) => sum + (product.pieces || 0),
                        0
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("products.totalCartons", "Total Cartons")}
                    >
                      {booking.products.reduce(
                        (sum, product) => sum + (product.cartons || 0),
                        0
                      )}
                    </Descriptions.Item>
                  </StyledDescriptions>
                </Card>
              </>
            ) : (
              <Alert message={t("bookings.noProducts")} type="info" showIcon />
            )}
          </StyledCard>
        </Space>
      </ContentContainer>
    </TrackerContainer>
  );
};
